import { apiFetch, apiUrl } from "./api";
import type { Audience } from "./broadcast-api";
import type { GeneratedDocument } from "./document-generator";
import type { SignatureBlock } from "./document-store";
import { ApiError } from "./incident-api";
import { maybeUpsellPlanLimit, PlanLimitError } from "./plan-limit-store";

export { ApiError };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let res: Response;
  try {
    res = await apiFetch(apiUrl(path), {
      credentials: "include",
      headers: { "Content-Type": "application/json", ...(init?.headers ?? {}) },
      ...init,
    });
  } catch (err) {
    if (err instanceof TypeError && /fetch/i.test(err.message)) {
      throw new ApiError("Can't reach the server.", 0);
    }
    throw err;
  }
  if (res.status === 204) return undefined as T;
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Request failed (${res.status})`;
    if (maybeUpsellPlanLimit(res.status, data)) throw new PlanLimitError(message);
    throw new ApiError(message, res.status);
  }
  return data as T;
}

/**
 * HR's editable draft. `publishedVersionId` points at what employees actually
 * read — editing a policy here changes nothing for them until it's published.
 */
export type Policy = {
  id: string;
  title: string;
  body: string;
  documentId: string | null;
  publishedVersionId: string | null;
  audience: Audience;
  requiresAck: boolean;
  published: boolean;
  createdAt: string;
  updatedAt: string;
};

/** An immutable published snapshot of the typed text and linked document. */
export type PolicyVersion = {
  id: string;
  policyId: string;
  version: number;
  title: string;
  body: string;
  documentId: string | null;
  documentName: string | null;
  documentTypeName: string | null;
  documentOutput: GeneratedDocument | null;
  documentSignatures: SignatureBlock[] | null;
  audience: Audience;
  requiresAck: boolean;
  note: string | null;
  createdAt: string;
  createdBy: string | null;
};

export type PolicyAttachment = {
  id: string;
  policyId: string;
  fileName: string;
  url: string;
  contentType: string;
  sizeBytes: number | null;
  /** True for the server-rendered policy PDF (vs. an HR upload). */
  isGenerated: boolean;
  createdAt: string;
};

export type UpsertPolicyBody = {
  title: string;
  body?: string;
  documentId?: string | null;
  audience: Audience;
  requiresAck?: boolean;
};

export type PolicyAckReport = {
  targets: Array<{
    employeeId: string;
    name: string;
    acknowledgedAt: string | null;
  }>;
  acknowledged: number;
  total: number;
  version: number | null;
};

export function listPolicies(): Promise<Policy[]> {
  return request<Policy[]>("/api/policies");
}

/** Filters + paging for the policies list (server-side). */
export type PoliciesPageParams = {
  limit: number;
  offset: number;
  q?: string;
  published?: boolean;
};

/** One page of policies plus the overall total for the active filters. */
export type PoliciesPage = { items: Policy[]; total: number };

/** A filtered, paginated slice of the owner's policies (`/api/policies/page`). */
export function fetchPoliciesPage(params: PoliciesPageParams): Promise<PoliciesPage> {
  const search = new URLSearchParams({
    limit: String(params.limit),
    offset: String(params.offset),
  });
  if (params.q) search.set("q", params.q);
  if (params.published !== undefined) search.set("published", String(params.published));
  return request<PoliciesPage>(`/api/policies/page?${search.toString()}`);
}

export function getPolicy(id: string): Promise<Policy> {
  return request<Policy>(`/api/policies/${id}`);
}

export function createPolicy(body: UpsertPolicyBody): Promise<Policy> {
  return request<Policy>("/api/policies", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

/** Saves the draft only — never changes what employees see. */
export function updatePolicy(id: string, body: Partial<UpsertPolicyBody>): Promise<Policy> {
  return request<Policy>(`/api/policies/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export function deletePolicy(id: string): Promise<{ ok: boolean }> {
  return request(`/api/policies/${id}`, { method: "DELETE" });
}

/* ── Versions ────────────────────────────────────────────────────────────── */

export type PublishPolicyBody = {
  note?: string;
};

/** Cuts a new immutable version and makes it the one employees read. */
export function publishPolicy(id: string, body: PublishPolicyBody = {}): Promise<PolicyVersion> {
  return request<PolicyVersion>(`/api/policies/${id}/publish`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export function listPolicyVersions(id: string): Promise<{ items: PolicyVersion[] }> {
  return request<{ items: PolicyVersion[] }>(`/api/policies/${id}/versions`);
}

export function getPolicyVersion(
  id: string,
  versionId: string,
): Promise<PolicyVersion & { files: PolicyAttachment[] }> {
  return request<PolicyVersion & { files: PolicyAttachment[] }>(
    `/api/policies/${id}/versions/${versionId}`,
  );
}

/** Stages an old version's text back onto the draft. Does not republish. */
export function restorePolicyVersion(id: string, versionId: string): Promise<Policy> {
  return request<Policy>(`/api/policies/${id}/restore/${versionId}`, {
    method: "POST",
  });
}

/* ── Attachments ─────────────────────────────────────────────────────────── */

export function listPolicyFiles(id: string): Promise<{ items: PolicyAttachment[] }> {
  return request<{ items: PolicyAttachment[] }>(`/api/policies/${id}/files`);
}

/**
 * Uploads one file (multipart). No `Content-Type` header — the browser must set
 * the multipart boundary itself. The server re-validates type and size and, on
 * top of that, verifies the file's magic bytes; the client-side checks that
 * mirror this are for fast feedback only.
 */
export async function uploadPolicyFile(id: string, file: File): Promise<PolicyAttachment> {
  const form = new FormData();
  form.append("file", file);
  const res = await apiFetch(apiUrl(`/api/policies/${id}/files/upload`), {
    method: "POST",
    credentials: "include",
    body: form,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) {
    const message =
      (data && typeof data === "object" && "error" in data
        ? String((data as { error: unknown }).error)
        : null) ?? `Upload failed (${res.status})`;
    throw new ApiError(message, res.status);
  }
  return (data as { item: PolicyAttachment }).item;
}

export function deletePolicyFile(id: string, fileId: string): Promise<void> {
  return request<void>(`/api/policies/${id}/files/${fileId}`, {
    method: "DELETE",
  });
}

export function getPolicyAckReport(id: string): Promise<PolicyAckReport> {
  return request<PolicyAckReport>(`/api/policies/${id}/acknowledgments`);
}
